import '@fpsm/cxy-tokens/tokens.css';
import '@fpsm/cxy-components/Pagination.css';

import { Pagination } from '@fpsm/cxy-components/Pagination';

import { type DeviceType, type Repub } from '@cx-modules/types';
import { mergePersistentParams } from '@cx-modules/utils';

import { type ArticleTypeName, type PaginationType } from '../../types';
import type { ArticleItem } from '../../types/article.types';
import { ArticleLink } from '../articles-link';
import { HeroArticle } from '../hero-article';
import { LoadingIndicator } from '../LoadingIndicator';

import {
  activePageLink,
  ArrowLink,
  Container,
  Footer,
  Header,
  HeadingLink,
  ItemsContainer,
  LoadMoreButtonLink,
  pageLink,
  paginationBreak,
  paginationBreakLink,
} from './style';

const featuredDefaultArticle = {
  FIRST_ARTICLE: 'FIRST_ARTICLE',
  NONE: 'NONE',
} as const;

export type DefaultFeaturedArticle = 'FIRST_ARTICLE' | 'NONE';

export type MoreArticlePassthroughProps = {
  articlesPage: number;
  baseUrl?: string;
  defaultFeaturedArticle?: DefaultFeaturedArticle;
  deviceType: DeviceType;
  featuredArticleId?: string;
  hasNextPage?: boolean;
  isLoading?: boolean;
  isLoggedIn?: boolean;
  limit?: number;
  loadMoreArticles?: () => void;
  moreArticlesPath?: string;
  moreArticlesText?: string;
  offset?: number;
  pagination?: PaginationType;
  repub: Repub;
  requestUrl: string;
  title?: string;
  type?: ArticleTypeName;
};

export const MoreArticles: React.FC<
  MoreArticlePassthroughProps & {
    articles: ArticleItem[];
    paginationNextLabel?: string;
    paginationPrevLabel?: string;
    total: number;
  }
> = ({
  articles,
  articlesPage,
  baseUrl,
  defaultFeaturedArticle,
  deviceType,
  featuredArticleId,
  hasNextPage,
  isLoading,
  isLoggedIn,
  limit,
  loadMoreArticles,
  moreArticlesPath,
  moreArticlesText = 'More Picks',
  pagination,
  paginationNextLabel,
  paginationPrevLabel,
  repub,
  requestUrl,
  title,
  total,
  type,
}) => {
  const componentTheme = 'oddschecker';
  const paginationPageCount = Math.ceil(total / (limit ?? 10));
  const changeAndReloadPage = (page: { selected: number }) => {
    const pageToLoad = page.selected + 1;
    const url = new URL(window.location.href);
    if (pageToLoad === 1) {
      url.searchParams.delete('articlesPage');
    } else {
      url.searchParams.set('articlesPage', pageToLoad.toString());
    }
    window.location.href = mergePersistentParams(url.toString());
  };
  const featuredArticle = articles.find(article => article.id === featuredArticleId);

  const heroArticle =
    featuredArticle || (defaultFeaturedArticle === featuredDefaultArticle.FIRST_ARTICLE ? articles[0] : null);

  const articlesList = heroArticle ? articles.filter(article => article.id !== heroArticle.id) : articles;

  const MoreLink = () => {
    return moreArticlesPath ? (
      <ArrowLink data-testid="more-articles-link" href={mergePersistentParams(moreArticlesPath, requestUrl)}>
        {moreArticlesText}
        <svg fill="none" height="10" viewBox="0 0 11 10" width="11" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M4.00003 0.757359L8.24267 5L4.00003 9.24264"
            stroke="var(--cxy-color-interactive-secondary-subtle)"
            strokeWidth="1.5"
          />
        </svg>
      </ArrowLink>
    ) : null;
  };
  const LoadMoreLink = () => {
    if (hasNextPage) {
      return (
        <LoadMoreButtonLink data-testid="more-articles-link" disabled={isLoading} onClick={loadMoreArticles}>
          {isLoading ? <LoadingIndicator variant="light" /> : moreArticlesText}
        </LoadMoreButtonLink>
      );
    } else {
      return null;
    }
  };
  return (
    <Container data-fpsm-theme={componentTheme} data-testid="more-articles">
      {heroArticle && (
        <HeroArticle {...heroArticle} baseUrl={baseUrl} deviceType={deviceType} repub={repub} requestUrl={requestUrl} />
      )}
      {title && (
        <Header>
          <HeadingLink
            data-testid="more-articles-title"
            href={moreArticlesPath ? mergePersistentParams(moreArticlesPath, requestUrl) : undefined}
            isHref={!!moreArticlesPath}
            title={title}
          >
            {title}
          </HeadingLink>
        </Header>
      )}
      <ItemsContainer rows={articles.length}>
        {articlesList.map(article => (
          <ArticleLink
            {...article}
            articleType={type}
            baseUrl={baseUrl}
            isLoggedIn={isLoggedIn}
            key={article.id}
            repub={repub}
            requestUrl={requestUrl}
          />
        ))}
      </ItemsContainer>
      {pagination !== 'NO_PAGINATION' && (
        <Footer isCentered={!moreArticlesPath}>
          {pagination === 'BOTTOM_PAGE_NUMBERS' && paginationPageCount > 1 && (
            <Pagination
              activeLinkClassName={activePageLink}
              breakClassName={paginationBreak}
              breakLabel="..."
              breakLinkClassName={paginationBreakLink}
              forcePage={articlesPage - 1}
              hrefAllControls
              hrefBuilder={(pageIndex, pageCount, selectedPage) => {
                if (pageIndex < 1) return undefined;
                if (pageIndex === selectedPage + 1) return undefined;

                if (pageIndex === 1) {
                  if (typeof window !== 'undefined') {
                    const url = new URL(window.location.href);
                    url.searchParams.delete('articlesPage');
                    const params = url.searchParams.toString();
                    return params ? `${url.pathname}?${params}` : url.pathname;
                  }
                  const url = new URL(requestUrl);
                  url.searchParams.delete('articlesPage');
                  const params = url.searchParams.toString();
                  return params ? `${url.pathname}?${params}` : url.pathname;
                }

                if (typeof window !== 'undefined') {
                  const url = new URL(window.location.href);
                  url.searchParams.set('articlesPage', String(pageIndex));
                  return `${url.pathname}?${url.searchParams.toString()}`;
                }
                // SSR: use requestUrl to preserve current path and params
                const url = new URL(requestUrl);
                url.searchParams.set('articlesPage', String(pageIndex));
                return `${url.pathname}?${url.searchParams.toString()}`;
              }}
              marginPagesDisplayed={1}
              nextLabel={paginationNextLabel}
              onPageChange={changeAndReloadPage}
              pageCount={paginationPageCount}
              pageLinkClassName={pageLink}
              pageRangeDisplayed={1}
              previousLabel={paginationPrevLabel}
              selectedPageRel={null}
            />
          )}
          {pagination === 'MORE_ARTICLES_LINKS' && moreArticlesPath && <MoreLink />}
          {pagination === 'LOAD_MORE_BUTTON' && <LoadMoreLink />}
        </Footer>
      )}
    </Container>
  );
};
